// @flow
import { isBrowser } from '@finect/front-utils/isBrowser';

const PROMOTION_TTL = 30 * 60 * 1000; // 30 minutos
const PROMOTION_KEY = 'fi_plus_promotion';

/**
 * Guarda de qué campaña y de qué creatividad viene el usuario.
 *
 * La atribución tiene que sobrevivir a la navegación hasta la compra, que ocurre en otra
 * página, así que viaja por sessionStorage. Escribir aquí —y no en cada llamador— mantiene
 * en un solo módulo la clave, la forma del blob y el `timestamp` del que depende el TTL:
 * un escritor que se olvide de estamparlo deja la atribución sin caducar para siempre.
 *
 * @param creativeName  Creatividad o CTA de origen
 * @param promotionName Campaña a la que pertenece
 */
export const setPlusPromotion = (creativeName: string, promotionName: string) => {
  if (!isBrowser()) return;

  try {
    sessionStorage.setItem(
      PROMOTION_KEY,
      // Claves en snake_case porque es el contrato de GA4, no estilo nuestro.
      // eslint-disable-next-line camelcase
      JSON.stringify({ creative_name: creativeName, promotion_name: promotionName, timestamp: Date.now() })
    );
  } catch (error) {
    // sessionStorage puede fallar en modo privado: se pierde la atribución, nada más.
  }
};

/**
 * Olvida la campaña de origen. Se llama al completar la compra, cuando la atribución ya se
 * ha enviado y mantenerla solo serviría para contaminar la siguiente sesión.
 */
export const clearPlusPromotion = () => {
  if (!isBrowser()) return;

  try {
    sessionStorage.removeItem(PROMOTION_KEY);
  } catch (error) {
    // Igual que arriba: si no se puede limpiar, el TTL la acaba caducando.
  }
};

/**
 * Si esta compra hay que contarla todavía, marcándola como contada.
 *
 * La vuelta de Stripe se puede recargar, así que el `purchase` se deduplica por el id de la sesión de
 * pago. Vive aquí, con el resto del acceso a sessionStorage, porque en el checkout se leía a pelo: en
 * modo privado o en un webview la lectura LANZA, y al hacerlo dentro del onSuccess de la confirmación
 * se perdía el evento (y la limpieza de la atribución).
 *
 * Si el almacén no está disponible se envía: perder una compra es peor que un duplicado, y GA4
 * deduplica por `transaction_id`.
 *
 * @param sessionId Id de la sesión de pago de Stripe
 */
export const shouldSendPurchase = (sessionId: string): boolean => {
  const key = `ga4_purchase_sent_${sessionId}`;

  try {
    if (sessionStorage.getItem(key)) return false;

    sessionStorage.setItem(key, 'true');

    return true;
  } catch (error) {
    return true;
  }
};

const getPromotionData = () => {
  if (!isBrowser()) return {};
  try {
    const raw = sessionStorage.getItem(PROMOTION_KEY);
    if (raw) {
      const data = JSON.parse(raw);

      if (data.timestamp && Date.now() - data.timestamp > PROMOTION_TTL) {
        sessionStorage.removeItem(PROMOTION_KEY);
        return {};
      }

      return {
        ...data.creative_name ? { creative_name: data.creative_name } : {},
        ...data.promotion_name ? { promotion_name: data.promotion_name } : {}
      };
    }
  } catch (e) {}
  return {};
};

/**
 * Genera un objeto de item siguiendo el estándar de GA4 para Finect Plus.
 *
 * @param {string} plan - 'monthly' o 'yearly'
 * @param {number} price - Precio final a pagar
 * @param {number|null} originalPrice - Precio original sin descuentos
 * @returns {Object} item de ecommerce GA4
 */
export const getPlusItem = (plan: string, price: number, originalPrice: ?number) => ({
  item_id: plan === 'yearly' ? 'plus-yearly' : 'plus',
  item_name: `Finect Plus${plan === 'yearly' ? ' Anual' : ' Mensual'}`,
  item_brand: 'Finect',
  item_category: 'Suscripción',
  item_variant: plan === 'yearly' ? 'Anual' : 'Mensual',
  price,
  ...originalPrice ? { discount: Number((originalPrice - price).toFixed(2)) } : {},
  quantity: 1,
  ...getPromotionData()
});
